import React, { useEffect, useState, useRef } from 'react';
import { arrayOf, string, number, bool } from 'prop-types';
import classnames from 'classnames';
import { useIntersectionObserver } from '@fiverr-private/hooks';
import { I18n } from '@fiverr-private/i18n-react';
import pathfinder from '@fiverr-private/pathfinder';
import { Icon } from '@fiverr-private/fit';
import { getContext } from '@fiverr-private/fiverr_context';
import { l_arrow_right } from '@fiverr-private/icons';
import { events, EVENT_NAMES } from '../../utils/events';
import projectItemType from '../../utils/types/projectItem';
import sellerType from '../../utils/types/seller';
import PortfolioPresenceHeader from '../common/PresenceHeader';
import PortfolioModal from '../common/Modal/lazy';
import { MAX_PROJECTS_IN_GRID, MAX_PROJECTS_MOBILE_GRID } from './ProjectGrid/constants';
import PortfolioPresenceProjectGrid from './ProjectGrid';
import './style.scss';

const PortfolioPresence = ({
    projectList,
    seller,
    origin = '',
    totalProjects,
    isMTEnabled,
    isWideTheme = false,
    gigId,
    portfolioLink,
    infinityPaging,
    educate,
    newTab = false,
    portfolioTitle
}) => {
    const [ selectedProjectId, selectProject ] = useState();
    const link = portfolioLink || pathfinder('user_portfolio_index', { user_id: seller.username }, { query: { origin } });
    const target = newTab ? '_blank' : '_self';
    const selectProjectWithEvent = (projectId, mixpanelEvent, biEvent) => {
        selectProject(projectId);
        events.projectClick({ projectId, mixpanelEvent, biEvent, seller, origin, projectList });
    };

    const observableRef = useRef(null);
    const entry = useIntersectionObserver(observableRef, { threshold: 1 });
    const isVisible = Boolean(entry?.isIntersecting);

    useEffect(() => {
        if (!isVisible) { return; }

        const { isMobile } = getContext();
        const maxProjects = isMobile ? MAX_PROJECTS_MOBILE_GRID : MAX_PROJECTS_IN_GRID;
        const projectsShown = Math.min(maxProjects, projectList.length);

        events.componentViewed(projectsShown, seller, origin, gigId);
    }, [gigId, origin, projectList.length, seller, isVisible]);

    if (!projectList?.length) {
        return null;
    }

    const headerProps = {
        tooltipProps: { k: 'portfolio_presence.tooltip', params: { sellerName: seller.username } },
        link,
        educate,
        portfolioTitle
    };

    const modalProps = {
        projectList,
        selectProject: (projectId) => selectProject(projectId),
        projectId: selectedProjectId,
        origin,
        seller,
        isMTEnabled,
        gigId,
        infinityPaging
    };

    const onSeeAllProjectsClick = () => {
        events.seeProjectsClick(seller, origin);
    };

    return (
        <section ref={observableRef} className={classnames('portfolio-presence', { 'slim-view': !isWideTheme })}>
            <PortfolioPresenceHeader {...headerProps}/>
            <PortfolioPresenceProjectGrid projectList={projectList} isWideTheme={isWideTheme}
                selectProject={(projectId) => selectProjectWithEvent(projectId, EVENT_NAMES.GRID_CLICK, EVENT_NAMES.BI_PORTFOLIO_PROJECT_CLICK)}/>
            <a className="portfolio-link" href={link} target={target} onClick={onSeeAllProjectsClick}>
                <span className="text-part m-r-8">
                    <I18n k="portfolio_presence.goto"/>&nbsp;({totalProjects})
                </span>
                <Icon className="arrow-link" size={18}>{l_arrow_right}</Icon>
            </a>
            <PortfolioModal {...modalProps}/>
        </section>
    );
};

PortfolioPresence.propTypes = {
    projectList: arrayOf(projectItemType).isRequired,
    seller: sellerType.isRequired,
    origin: string,
    totalProjects: number,
    isMTEnabled: bool,
    isWideTheme: bool,
    gigId: number,
    portfolioLink: string,
    infinityPaging: bool,
    educate: bool,
    newTab: bool,
    portfolioTitle: string
};

export default PortfolioPresence;
